import { useEffect, useMemo, useRef } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CircleNotch, Warning, CaretRight } from '@phosphor-icons/react';

import { fetchSettingsManifest, type SettingsManifest } from '@/lib/settingsApi';
import { sectionMeta } from './catalogue';
import { contextLine, groupSections, sectionSummary, settingsBasePath } from './manifest';
import { BackButton } from '@/components/shared/BackButton';

/**
 * The Settings shell — one implementation, five experiences.
 *
 * Everything that differs between a solo barrister, a chambers barrister, a solo clerk, a
 * chambers clerk and a chambers administrator comes from ONE place: the server manifest. There
 * is no `if (isAdmin)` in this file or any file under it, no member-type array, and no inference
 * from how many barristers happen to be loaded. If the navigation is wrong, the registry or the
 * context resolver is wrong — and both are covered by tests — rather than some React branch
 * quietly disagreeing with the API.
 */

/**
 * Where Settings lives in the URL for this person — derived from the SERVER-resolved actor on
 * the manifest, never from the client's own reading of who they are.
 */
export function useSettingsBasePath(): string {
  const { data } = useSettingsManifest();
  return settingsBasePath(data?.context.actor ?? 'barrister');
}

export function useSettingsManifest() {
  return useQuery({
    queryKey: ['settings-manifest'],
    queryFn: fetchSettingsManifest,
    staleTime: 60_000,
  });
}

export function SettingsShell() {
  const { data: manifest, isLoading, isError, refetch } = useSettingsManifest();
  const basePath = useSettingsBasePath();
  const location = useLocation();
  const navigate = useNavigate();

  const groups = useMemo(() => (manifest ? groupSections(manifest) : []), [manifest]);

  /**
   * Keep the SELECTED section visible in the narrow-screen strip.
   *
   * On a phone the nav is one horizontally scrolling row, and My Account is its first chip.
   * Several settings pages (Integrations, Fees, Public profile, Practice areas, Prep checklist)
   * are routed OUTSIDE this shell, so returning from one rebuilds the strip at scrollLeft 0 —
   * the selected section sits off-screen to the right and the row looks as though it has jumped
   * back to My Account, leaving the person to scroll and hunt for where they already were.
   *
   * scrollLeft is set directly rather than via scrollIntoView, which would also move the PAGE.
   */
  const stripRef = useRef<HTMLDivElement>(null);
  const activeChipRef = useRef<HTMLAnchorElement | null>(null);

  useEffect(() => {
    const strip = stripRef.current;
    const chip = activeChipRef.current;
    if (!strip || !chip) return;
    // From lg up the nav is a vertical column — there is nothing to scroll sideways.
    if (strip.scrollWidth <= strip.clientWidth) return;
    const stripBox = strip.getBoundingClientRect();
    const chipBox = chip.getBoundingClientRect();
    strip.scrollLeft += chipBox.left - stripBox.left - (stripBox.width - chipBox.width) / 2;
  }, [location.pathname, groups]);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center" role="status" aria-label="Loading settings">
        <CircleNotch size={22} className="animate-spin text-muted-foreground/50" />
      </div>
    );
  }

  if (isError || !manifest) {
    return (
      <div className="mx-auto max-w-lg rounded-xl border border-border bg-card p-8 text-center">
        <Warning size={22} className="mx-auto mb-3 text-amber-600 dark:text-amber-400" weight="fill" />
        <p className="text-sm font-semibold text-foreground">Your settings could not be loaded</p>
        <p className="mt-1 text-sm text-muted-foreground">
          This is usually a connection problem rather than anything wrong with your account.
        </p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="mt-4 rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition hover:bg-muted"
        >
          Try again
        </button>
      </div>
    );
  }

  // An external actor has no Settings at all. Saying so plainly beats an empty frame.
  if (manifest.sections.length === 0) {
    return (
      <div className="mx-auto max-w-lg rounded-xl border border-border bg-card p-8 text-center">
        <p className="text-sm font-semibold text-foreground">There are no settings for this account</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Your account does not configure anything in this workspace.
        </p>
      </div>
    );
  }

  const isIndex = location.pathname === basePath || location.pathname === `${basePath}/`;

  return (
    <div>
      <BackButton onClick={() => navigate(-1)} />

      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">Settings</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">{contextLine(manifest)}</p>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
        {/* Navigation — a column on wide screens, a scrolling strip on narrow ones so it never
            collapses into an unusable stack on a phone. */}
        <nav
          aria-label="Settings sections"
          className="min-w-0 shrink-0 lg:w-60"
        >
          {/* The strip scrolls WITHIN itself on a phone; the page itself must never scroll
              sideways, so no negative margin is allowed to widen it past its parent. */}
          {/* `no-scrollbar`, as every other strip in the app does. A drawn scrollbar sits as a
              short grey bar under the FIRST chip whatever is selected — on a desktop browser at
              phone width, where scrollbars are not overlays, it reads as an underline marking
              the current section, and it marks the wrong one. The selected section is shown by
              the filled chip; the strip already scrolls that chip into view on arrival. */}
          <div ref={stripRef} className="no-scrollbar flex gap-1 overflow-x-auto overflow-y-hidden pb-2 lg:flex-col lg:gap-0 lg:overflow-visible lg:overflow-y-visible lg:pb-0">
            {groups.map(({ group, heading, sections }) => (
              <div key={group} className="contents lg:block lg:mb-5">
                <div className="hidden lg:mb-2 lg:block lg:px-3 lg:text-[11px] lg:font-semibold lg:uppercase lg:tracking-[0.08em] lg:text-muted-foreground">
                  {heading}
                </div>
                {sections.map((s) => (
                  <NavLink
                    key={s.id}
                    ref={location.pathname === `${basePath}/section/${s.id}` ? activeChipRef : undefined}
                    to={`${basePath}/section/${s.id}`}
                    className={({ isActive }) =>
                      [
                        'flex shrink-0 items-center justify-between gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm transition',
                        'lg:w-full lg:whitespace-normal',
                        isActive
                          ? 'bg-muted font-semibold text-foreground'
                          : 'font-medium text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                      ].join(' ')
                    }
                  >
                    <span>{s.label}</span>
                    <CaretRight className="hidden h-3.5 w-3.5 shrink-0 opacity-40 lg:block" />
                  </NavLink>
                ))}
              </div>
            ))}
          </div>
        </nav>

        <div className="min-w-0 flex-1 pb-16">
          {isIndex ? <SettingsOverview manifest={manifest} basePath={basePath} /> : <Outlet />}
        </div>
      </div>
    </div>
  );
}

/** The landing view: every section this person has, with an honest count of what it holds. */
function SettingsOverview({ manifest, basePath }: { manifest: SettingsManifest; basePath: string }) {
  const groups = groupSections(manifest);

  return (
    <div className="space-y-8">
      {groups.map(({ group, heading, sections }) => (
        <div key={group}>
          <div className="mb-3 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            {heading}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {sections.map((s) => {
              const section = manifest.sections.find((x) => x.id === s.id)!;
              const meta = sectionMeta(s.id);
              return (
                <NavLink
                  key={s.id}
                  to={`${basePath}/section/${s.id}`}
                  className="group rounded-xl border border-border bg-card p-4 transition hover:border-brand-600/40 hover:shadow-card"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-foreground">{meta.label}</div>
                      <div className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                        {meta.description}
                      </div>
                    </div>
                    <CaretRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-foreground" />
                  </div>
                  <div className="mt-3 text-[11px] font-medium text-muted-foreground">
                    {sectionSummary(section)}
                  </div>
                </NavLink>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
